/**
 * End-to-End encryption primitives for Golden TV Connect chat.
 *
 * Design:
 *   - Each user owns a long-lived ECDH P-256 keypair.
 *   - The private key is stored server-side WRAPPED (AES-GCM) with a key derived from
 *     the user's password via PBKDF2-SHA256 (200,000 iterations, 16-byte random salt).
 *   - On login the client fetches the wrapped blob, derives the unwrap key from the
 *     password + salt, and unwraps the private key. It never leaves the device.
 *   - For every peer, a shared AES-GCM 256 secret is derived via ECDH → HKDF-SHA256.
 *   - Text messages: AES-GCM(sharedKey, plaintext) with a fresh 12-byte IV.
 *   - Files: a random 32-byte fileKey encrypts the blob (AES-GCM). The fileKey is then
 *     wrapped with the peer's sharedKey and attached to the message metadata.
 */

const PBKDF2_ITERATIONS = 200_000;
const PBKDF2_SALT_BYTES = 16;
const AES_KEY_LEN = 256;
const AES_IV_BYTES = 12;
const ECDH_NAMED_CURVE = 'P-256';

// ---------- base64 helpers ----------
export const b64encode = (bytes) => {
  let bin = '';
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  for (let i = 0; i < arr.byteLength; i++) bin += String.fromCharCode(arr[i]);
  return btoa(bin);
};

export const b64decode = (str) => {
  const bin = atob(str);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};

const randomBytes = (n) => {
  const b = new Uint8Array(n);
  crypto.getRandomValues(b);
  return b;
};

const enc = new TextEncoder();
const dec = new TextDecoder();

// ---------- Password-derived wrapping key ----------
async function deriveWrappingKey(password, saltBytes) {
  const baseKey = await crypto.subtle.importKey(
    'raw', enc.encode(password), { name: 'PBKDF2' }, false, ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: saltBytes, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: AES_KEY_LEN },
    false,
    ['encrypt', 'decrypt']
  );
}

// ---------- Key generation & (un)wrapping ----------
export async function generateKeyPair() {
  return crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: ECDH_NAMED_CURVE },
    true,
    ['deriveKey', 'deriveBits']
  );
}

export async function exportPublicKeyB64(publicKey) {
  const spki = await crypto.subtle.exportKey('spki', publicKey);
  return b64encode(spki);
}

export async function importPeerPublicKey(b64) {
  const bytes = b64decode(b64);
  return crypto.subtle.importKey(
    'spki', bytes, { name: 'ECDH', namedCurve: ECDH_NAMED_CURVE }, true, []
  );
}

/**
 * Encrypt (wrap) the private key blob with a password-derived AES-GCM key.
 * Returns { wrapped_private_key, key_salt, key_iv } base64-encoded.
 */
export async function wrapPrivateKey(privateKey, password) {
  const pkcs8 = await crypto.subtle.exportKey('pkcs8', privateKey);
  const salt = randomBytes(PBKDF2_SALT_BYTES);
  const wrapKey = await deriveWrappingKey(password, salt);
  const iv = randomBytes(AES_IV_BYTES);
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, wrapKey, pkcs8);
  return {
    wrapped_private_key: b64encode(ct),
    key_salt: b64encode(salt),
    key_iv: b64encode(iv),
  };
}

/**
 * Decrypt (unwrap) the private key blob using the password. Returns a CryptoKey.
 * Throws if the password is wrong.
 */
export async function unwrapPrivateKey({ wrapped_private_key, key_salt, key_iv }, password) {
  const wrappedBytes = b64decode(wrapped_private_key);
  const saltBytes = b64decode(key_salt);
  const ivBytes = b64decode(key_iv);
  const wrapKey = await deriveWrappingKey(password, saltBytes);
  const pkcs8 = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: ivBytes }, wrapKey, wrappedBytes);
  return crypto.subtle.importKey(
    'pkcs8', pkcs8, { name: 'ECDH', namedCurve: ECDH_NAMED_CURVE }, true, ['deriveKey', 'deriveBits']
  );
}

// ---------- Peer shared key (ECDH -> HKDF -> AES-GCM key) ----------
export async function deriveSharedKey(ownPrivateKey, peerPublicKey) {
  // Derive raw bits (32 bytes) then run HKDF over them for domain separation.
  const rawBits = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: peerPublicKey }, ownPrivateKey, 256
  );
  const hkdfKey = await crypto.subtle.importKey('raw', rawBits, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(0), info: enc.encode('gtc:e2e:v1') },
    hkdfKey,
    { name: 'AES-GCM', length: AES_KEY_LEN },
    false,
    ['encrypt', 'decrypt']
  );
}

// ---------- Text encryption ----------
export async function encryptText(sharedKey, plaintext) {
  const iv = randomBytes(AES_IV_BYTES);
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, sharedKey, enc.encode(plaintext ?? ''));
  return { v: 1, iv: b64encode(iv), ct: b64encode(ct) };
}

export async function decryptText(sharedKey, payload) {
  if (!payload || !payload.iv || !payload.ct) throw new Error('invalid payload');
  const iv = b64decode(payload.iv);
  const ct = b64decode(payload.ct);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, sharedKey, ct);
  return dec.decode(pt);
}

// ---------- File encryption ----------
/**
 * Encrypts a Blob with a fresh AES-GCM 256 key and returns:
 *   - cipherBlob: a Blob (opaque bytes) that can be uploaded as-is
 *   - fileKey: the raw 32-byte AES key (Uint8Array) — MUST be wrapped for the peer
 *   - iv: the 12-byte IV (Uint8Array)
 */
export async function encryptBlob(blob) {
  const rawKey = randomBytes(32);
  const key = await crypto.subtle.importKey('raw', rawKey, { name: 'AES-GCM', length: AES_KEY_LEN }, false, ['encrypt']);
  const iv = randomBytes(AES_IV_BYTES);
  const buf = await blob.arrayBuffer();
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, buf);
  return {
    cipherBlob: new Blob([ct], { type: 'application/octet-stream' }),
    fileKey: rawKey,
    iv,
  };
}

export async function decryptBlob(cipherBlob, rawFileKey, ivBytes, outputMime = 'application/octet-stream') {
  const key = await crypto.subtle.importKey('raw', rawFileKey, { name: 'AES-GCM', length: AES_KEY_LEN }, false, ['decrypt']);
  const buf = await cipherBlob.arrayBuffer();
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: ivBytes }, key, buf);
  return new Blob([pt], { type: outputMime || 'application/octet-stream' });
}

/**
 * Wrap a raw file-key (Uint8Array of 32 bytes) with the peer shared AES-GCM key.
 * Returns { file_key_wrapped, file_key_iv } base64.
 */
export async function wrapFileKey(sharedKey, rawFileKey) {
  const iv = randomBytes(AES_IV_BYTES);
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, sharedKey, rawFileKey);
  return { file_key_wrapped: b64encode(ct), file_key_iv: b64encode(iv) };
}

export async function unwrapFileKey(sharedKey, wrappedB64, ivB64) {
  const ct = b64decode(wrappedB64);
  const iv = b64decode(ivB64);
  const raw = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, sharedKey, ct);
  return new Uint8Array(raw);
}

// ---------- Feature detection ----------
export function isE2ESupported() {
  return typeof crypto !== 'undefined'
      && !!crypto.subtle
      && typeof crypto.subtle.generateKey === 'function';
}
